import { PlacedBlock, SocketConnection, BlockSystem } from '@/app/types/cad';
import { generateSystemColor } from '@/app/utils/transform';

/**
 * ============================================================================
 * SIMPLE & ROBUST SYSTEM DETECTION v3.0
 * ============================================================================
 * 
 * RULES:
 * 1. NEVER delete connections or blocks - only work with what exists
 * 2. Skip invalid connections silently - don't crash
 * 3. Preserve all existing system IDs and data
 * 4. Multiple systems can coexist peacefully
 * 5. Simple, linear, predictable
 */

/**
 * Helper: Extract block IDs from a connection (supports both formats)
 */
function getConnectionBlockIds(conn: SocketConnection): string[] {
  // NEW FORMAT: connectedSockets array
  if (conn.connectedSockets && conn.connectedSockets.length > 0) {
    return conn.connectedSockets.map(s => s.blockId);
  }
  // LEGACY FORMAT: sourceBlockId + targetBlockId
  if (conn.sourceBlockId && conn.targetBlockId) {
    return [conn.sourceBlockId, conn.targetBlockId];
  }
  return [];
}

/**
 * Main function: Detect all systems from current blocks and connections
 * 
 * @returns Updated blocks with systemId set, and all systems detected
 */
export function detectSystems(
  blocks: PlacedBlock[],
  connections: SocketConnection[],
  existingSystems: BlockSystem[]
): { blocks: PlacedBlock[]; systems: BlockSystem[] } {
  
  console.log('🔍 SYSTEM DETECTION v3.0: Starting...', {
    blocks: blocks.length,
    connections: connections.length,
    existingSystems: existingSystems.length
  });

  // STEP 1: Build adjacency graph from connections
  const blockIdSet = new Set(blocks.map(b => b.id));
  const graph = new Map<string, Set<string>>();
  
  blocks.forEach(block => {
    graph.set(block.id, new Set());
  });

  let skippedConnections = 0;
  connections.forEach(conn => {
    // Extract block IDs (supports both new and legacy formats)
    const connectedBlockIds = getConnectionBlockIds(conn)
      .filter(blockId => blockIdSet.has(blockId)); // Only valid blocks
    
    // Skip if not enough valid blocks
    if (connectedBlockIds.length < 2) {
      skippedConnections++;
      return;
    }
    
    // Add edges between all connected blocks (usually 2, but could be more)
    for (let i = 0; i < connectedBlockIds.length; i++) {
      for (let j = i + 1; j < connectedBlockIds.length; j++) {
        const blockA = connectedBlockIds[i];
        const blockB = connectedBlockIds[j];
        
        graph.get(blockA)?.add(blockB);
        graph.get(blockB)?.add(blockA);
      }
    }
  });

  if (skippedConnections > 0) {
    console.log(`  ⏭️ Skipped ${skippedConnections} connections (invalid blocks)`);
  }

  // STEP 2: Find connected components using DFS
  const visited = new Set<string>();
  const components: string[][] = [];

  const dfs = (blockId: string, component: string[]) => {
    visited.add(blockId);
    component.push(blockId);

    const neighbors = graph.get(blockId);
    if (neighbors) {
      neighbors.forEach(neighborId => {
        if (!visited.has(neighborId)) {
          dfs(neighborId, component);
        }
      });
    }
  };

  blocks.forEach(block => {
    if (!visited.has(block.id)) {
      const component: string[] = [];
      dfs(block.id, component);
      
      // Only components with 2+ blocks form a system
      if (component.length >= 2) {
        components.push(component);
      }
    }
  });

  console.log(`  🔍 Found ${components.length} connected components (systems)`);

  // STEP 3: Create or update systems for each component
  const detectedSystems: BlockSystem[] = [];
  const blockToSystemMap = new Map<string, string>();

  components.forEach((componentBlockIds, index) => {
    // Check which existing system IDs these blocks reference
    const existingSystemIds = new Set<string>();
    componentBlockIds.forEach(blockId => {
      const block = blocks.find(b => b.id === blockId);
      if (block?.systemId) {
        existingSystemIds.add(block.systemId);
      }
    });

    let system: BlockSystem;

    if (existingSystemIds.size === 0) {
      // NEW SYSTEM - no blocks had a system ID
      const systemId = `system-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      system = {
        id: systemId,
        name: `System ${detectedSystems.length + 1}`,
        color: generateSystemColor(),
        blockIds: componentBlockIds,
        connectionIds: connections
          .filter(c => {
            const connBlockIds = getConnectionBlockIds(c);
            return connBlockIds.length > 0 && connBlockIds.every(bid => componentBlockIds.includes(bid));
          })
          .map(c => c.id),
        attributes: []
      };
      
      console.log(`  ✅ NEW: ${system.name} with ${componentBlockIds.length} blocks`);

    } else if (existingSystemIds.size === 1) {
      // UPDATE EXISTING - all blocks reference the same system
      const systemId = Array.from(existingSystemIds)[0];
      const existingSystem = existingSystems.find(s => s.id === systemId);
      
      if (existingSystem) {
        // Preserve existing system, update blocks/connections
        system = {
          ...existingSystem, // Keep name, color, attributes, etc.
          blockIds: componentBlockIds,
          connectionIds: connections
            .filter(c => {
              const connBlockIds = getConnectionBlockIds(c);
              return connBlockIds.length > 0 && connBlockIds.every(bid => componentBlockIds.includes(bid));
            })
            .map(c => c.id)
        };
        
        console.log(`  🔄 UPDATE: ${system.name} with ${componentBlockIds.length} blocks`);
      } else {
        // System ID referenced but system not found - create new with that ID
        system = {
          id: systemId,
          name: `System ${detectedSystems.length + 1}`,
          color: generateSystemColor(),
          blockIds: componentBlockIds,
          connectionIds: connections
            .filter(c => {
              const connBlockIds = getConnectionBlockIds(c);
              return connBlockIds.length > 0 && connBlockIds.every(bid => componentBlockIds.includes(bid));
            })
            .map(c => c.id),
          attributes: []
        };
        
        console.log(`  ⚠️ RECREATE: System ${systemId} (was lost)`);
      }

    } else {
      // MERGE - blocks reference multiple different systems
      const systemsToMerge = existingSystems.filter(s => existingSystemIds.has(s.id));
      const primarySystem = systemsToMerge[0];
      
      system = {
        id: primarySystem.id,
        name: systemsToMerge.map(s => s.name).join(' + '),
        color: primarySystem.color,
        blockIds: componentBlockIds,
        connectionIds: connections
          .filter(c => {
            const connBlockIds = getConnectionBlockIds(c);
            return connBlockIds.length > 0 && connBlockIds.every(bid => componentBlockIds.includes(bid));
          })
          .map(c => c.id),
        attributes: systemsToMerge.flatMap(s => s.attributes || [])
      };
      
      console.log(`  🔗 MERGE: ${systemsToMerge.length} systems → ${system.name}`);
    }

    detectedSystems.push(system);
    
    // Map all blocks in this component to this system
    componentBlockIds.forEach(blockId => {
      blockToSystemMap.set(blockId, system.id);
    });
  });

  // STEP 4: Update blocks with system IDs
  const updatedBlocks = blocks.map(block => {
    const newSystemId = blockToSystemMap.get(block.id);
    
    // Only create new object if systemId changed (performance)
    if (block.systemId !== newSystemId) {
      return { ...block, systemId: newSystemId };
    }
    
    return block;
  });

  console.log('✅ SYSTEM DETECTION v3.0: Complete!', {
    systems: detectedSystems.length,
    systemNames: detectedSystems.map(s => `${s.name} (${s.blockIds.length} blocks)`)
  });

  return {
    blocks: updatedBlocks,
    systems: detectedSystems
  };
}